import React, {useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import Icon from './icons/Icon';
import { getAdkSession, getSessionChildren } from '../services/AdkSessionsService';
import { extractToolCallSteps, bucketToolCallSteps } from '../utils/sessionSteps';
import { describeTool } from '../utils/toolTitles';
import './SessionStepsDrawer.css';

// Same sentence-per-tool catalog the live view uses (utils/toolTitles.js).
function describeStep(step) {
  const { verb, summary } = describeTool(step.name, step.args);
  return { verb, summary };
}

function formatDuration(startedAt, endedAt) {
  if (!startedAt || !endedAt) return null;
  const ms = endedAt - startedAt;
  return ms < 1000 ? `${Math.round(ms)}ms` : `${(ms / 1000).toFixed(1)}s`;
}

function StepColumn({title, steps, emptyLabel, renderStep}) {
  return (
    <div className="session-steps-column">
      <div className="session-steps-column-header">
        <span>{title}</span>
        <span className="session-steps-column-count">{steps.length}</span>
      </div>
      <div className="session-steps-column-list">
        {steps.length === 0 && <div className="session-steps-empty">{emptyLabel}</div>}
        {steps.map(renderStep)}
      </div>
    </div>
  );
}

StepColumn.propTypes = {
  emptyLabel: PropTypes.string.isRequired,
  renderStep: PropTypes.func.isRequired,
  steps: PropTypes.array.isRequired,
  title: PropTypes.string.isRequired,
};

/**
 * Slide-over drawer opened by clicking a card in the History page's Kanban
 * view — shows that session's own tool-call steps bucketed into
 * To do/In progress/Done, and — when the session spawned any `parallel`/
 * `fork` child sessions (see AdkSessionsService.getSessionChildren) — a
 * "Workflow steps" section listing each child as its own step. Fetches
 * lazily on open rather than up front for every card, since a full event
 * log per session would be wasteful for a board the user is just scanning.
 */
export default function SessionStepsDrawer({session, userId, onClose, onOpenSession}) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [steps, setSteps] = useState({todo: [], wip: [], done: []});
  const [children, setChildren] = useState([]);

  useEffect(() => {
    if (!session) return undefined;
    let cancelled = false;
    setLoading(true);
    setError(null);
    Promise.all([
      getAdkSession(session.appName, userId, session.id),
      getSessionChildren(session.appName, userId, session.id).catch(() => []),
    ]).then(([full, childList]) => {
      if (cancelled) return;
      const parsed = extractToolCallSteps(full?.events || []);
      setSteps(bucketToolCallSteps(parsed));
      setChildren(childList);
    }).catch((err) => {
      if (!cancelled) setError(err?.message || 'Failed to load session steps');
    }).finally(() => {
      if (!cancelled) setLoading(false);
    });
    return () => { cancelled = true; };
  }, [session, userId]);

  if (!session) return null;

  const renderToolStep = (step) => {
    const {verb, summary} = describeStep(step);
    const duration = formatDuration(step.startedAt, step.endedAt);
    const status = step.response === undefined ? 'wip' : (step.failed ? 'error' : 'done');
    return (
      <div className={`session-steps-step is-${status}`} key={step.id}>
        <span className="session-steps-step-dot" />
        <div className="session-steps-step-body">
          <div className="session-steps-step-title">
            {verb}
            {duration && <span className="session-steps-step-duration">{duration}</span>}
          </div>
          {summary && <div className="session-steps-step-summary">{summary}</div>}
        </div>
      </div>
    );
  };

  const renderChildStep = (child) => (
    <div className={`session-steps-step is-${child.isActive ? 'wip' : 'done'}`} key={child.id}>
      <span className="session-steps-step-dot" />
      <div className="session-steps-step-body">
        <div className="session-steps-step-title">
          {child.name || 'Untitled step'}
          <span className="session-steps-child-relation">{child.relation}</span>
        </div>
      </div>
      <button
        className="session-steps-child-open"
        onClick={() => onOpenSession({...child, id: child.id, appName: child.appName})}
        title="Open this step's conversation"
        type="button"
      >
        <Icon name="external-link" size={12} />
      </button>
    </div>
  );

  return (
    <div className="session-steps-overlay" onClick={onClose}>
      <div className="session-steps-drawer" onClick={(e) => e.stopPropagation()}>
        <div className="session-steps-header">
          <div className="session-steps-title">{session.name || 'Untitled Conversation'}</div>
          <button className="session-steps-close" onClick={onClose} title="Close" type="button">
            <Icon name="close" size={16} />
          </button>
        </div>

        {loading ? (
          <div className="session-steps-loading">Loading steps…</div>
        ) : error ? (
          <div className="session-steps-error">{error}</div>
        ) : (
          <>
            {children.length > 0 && (
              <div className="session-steps-workflow-section">
                <div className="session-steps-section-label">
                  <Icon name="template" size={13} />
                  Workflow steps
                </div>
                <div className="session-steps-workflow-list">
                  {children.map(renderChildStep)}
                </div>
              </div>
            )}

            <div className="session-steps-columns">
              <StepColumn
                emptyLabel="Nothing queued"
                renderStep={renderToolStep}
                steps={steps.todo}
                title="To do"
              />
              <StepColumn
                emptyLabel="Nothing in progress"
                renderStep={renderToolStep}
                steps={steps.wip}
                title="In progress"
              />
              <StepColumn
                emptyLabel="No steps yet"
                renderStep={renderToolStep}
                steps={steps.done}
                title="Done"
              />
            </div>
          </>
        )}

        <div className="session-steps-footer">
          <button className="session-steps-open-btn" onClick={() => onOpenSession(session)} type="button">
            Open conversation
            <Icon name="arrow-right" size={13} />
          </button>
        </div>
      </div>
    </div>
  );
}

SessionStepsDrawer.propTypes = {
  onClose: PropTypes.func.isRequired,
  onOpenSession: PropTypes.func.isRequired,
  session: PropTypes.shape({
    appName: PropTypes.string,
    id: PropTypes.string,
    name: PropTypes.string,
  }),
  userId: PropTypes.string,
};

SessionStepsDrawer.defaultProps = {
  session: null,
  userId: null,
};
